import { createTheme, type MantineColorsTuple, type MantineThemeOverride } from '@mantine/core';

/** The default palette: an aviation navy that reads well on a sunlit tablet at the airfield. */
const aeroBlue: MantineColorsTuple = [
  '#eef4fb',
  '#dbe6f3',
  '#b3cbe8',
  '#89aedd',
  '#6795d3',
  '#5285cd',
  '#457dcb',
  '#366bb4',
  '#2c5fa2',
  '#1e5190',
];

const runway: MantineColorsTuple = [
  '#f4f5f7',
  '#e7e8ea',
  '#cbcdd3',
  '#adb1bb',
  '#9399a7',
  '#838a9b',
  '#7a8296',
  '#687083',
  '#5c6476',
  '#4d576b',
];

export const SEVERITY_COLORS = {
  INFO: 'blue',
  WARNING: 'orange',
  CRITICAL: 'red',
} as const;

/** Maintenance due statuses map onto the same traffic light the alerts use. */
export const DUE_STATUS_COLORS = {
  OK: 'teal',
  DUE_SOON: 'yellow',
  DUE: 'orange',
  IN_TOLERANCE: 'orange',
  OVERDUE: 'red',
} as const;

export const VALIDITY_COLORS = {
  VALID: 'teal',
  EXPIRING: 'orange',
  EXPIRED: 'red',
  SUSPENDED: 'grape',
  MISSING: 'gray',
} as const;

export const baseTheme: MantineThemeOverride = createTheme({
  primaryColor: 'aero',
  primaryShade: { light: 7, dark: 5 },
  colors: { aero: aeroBlue, runway },
  defaultRadius: 'md',
  fontFamily:
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontFamilyMonospace:
    '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  headings: {
    fontWeight: '650',
    sizes: {
      h1: { fontSize: '1.75rem', lineHeight: '1.25' },
      h2: { fontSize: '1.375rem', lineHeight: '1.3' },
      h3: { fontSize: '1.125rem', lineHeight: '1.35' },
    },
  },
  other: {
    // Registrations, ICAO codes and counter readings are compared by eye down a column, so
    // they are always tabular and monospaced.
    identifierFontFamily:
      '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  components: {
    Card: { defaultProps: { withBorder: true, shadow: 'none', radius: 'md' } },
    Paper: { defaultProps: { withBorder: true, shadow: 'none' } },
    Table: { defaultProps: { highlightOnHover: true, verticalSpacing: 'xs' } },
    Badge: { defaultProps: { variant: 'light', radius: 'sm' } },
    Button: { defaultProps: { variant: 'filled' } },
    Modal: { defaultProps: { centered: true, overlayProps: { blur: 2 } } },
    Tooltip: { defaultProps: { withArrow: true, openDelay: 300 } },
    TextInput: { defaultProps: { autoComplete: 'off' } },
  },
});

/**
 * Rebuilds the theme around an organisation's own colour.
 *
 * Each club gets its branding without a separate build: the hex from the organisation record
 * is expanded into a Mantine tuple at runtime.
 */
export function themeForOrganisation(primaryColor?: string | null): MantineThemeOverride {
  if (!primaryColor || !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(primaryColor)) {
    return baseTheme;
  }
  return createTheme({
    ...baseTheme,
    colors: { ...baseTheme.colors, aero: tupleFromHex(primaryColor) },
  });
}

function tupleFromHex(hex: string): MantineColorsTuple {
  const { h, s, l } = hexToHsl(hex);
  // Mantine expects ten shades from lightest to darkest; anchoring lightness on a curve keeps
  // contrast usable whatever colour a club picks, including very dark or very pale ones.
  const lightnesses = [96, 91, 82, 72, 64, 58, 53, 46, 39, 32];
  const shades = lightnesses.map((target, index) => {
    const saturation = index < 2 ? Math.max(s * 0.55, 12) : s;
    return hslToHex(h, saturation, index === 6 ? l : target);
  });
  return shades as unknown as MantineColorsTuple;
}

function hexToHsl(hex: string) {
  let value = hex.replace('#', '');
  if (value.length === 3) {
    value = value
      .split('')
      .map((char) => char + char)
      .join('');
  }
  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = ((max + min) / 2) * 100;
  const s = delta === 0 ? 0 : (delta / (1 - Math.abs((2 * l) / 100 - 1))) * 100;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: Math.min(s, 100), l };
}

function hslToHex(h: number, s: number, l: number) {
  const saturation = s / 100;
  const lightness = l / 100;
  const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lightness - c / 2;

  const [r, g, b] = (() => {
    if (h < 60) return [c, x, 0];
    if (h < 120) return [x, c, 0];
    if (h < 180) return [0, c, x];
    if (h < 240) return [0, x, c];
    if (h < 300) return [x, 0, c];
    return [c, 0, x];
  })();

  const toHex = (channel: number) =>
    Math.round((channel + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
